// Compact two-line round result — sits between dice and the Lightning reel.
import type { I18n } from '@aerofruits/i18n';
import { formatLightningBonusTenths, formatMultiplierTenths } from '../game/lightning-types.js';

export interface ResultBlockBounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

export class ResultBlock {
  private readonly root: HTMLElement;
  private readonly line1: HTMLElement;
  private readonly line2: HTMLElement;
  private readonly live: HTMLElement;
  private readonly i18n: I18n;
  private readonly formatMoney: (minor: number, currency: string) => string;

  constructor(
    root: HTMLElement,
    opts: {
      i18n: I18n;
      formatMoney: (minor: number, currency: string) => string;
    },
  ) {
    this.root = root;
    this.i18n = opts.i18n;
    this.formatMoney = opts.formatMoney;

    root.classList.add('lightning-result');
    root.setAttribute('role', 'status');
    root.setAttribute('aria-live', 'polite');
    root.setAttribute('aria-atomic', 'true');
    root.innerHTML = `
      <p class="lightning-result__line lightning-result__line--1" id="lightning-result-line1"></p>
      <p class="lightning-result__line lightning-result__line--2" id="lightning-result-line2"></p>
      <div class="sr-only" id="lightning-result-live" aria-live="polite" aria-atomic="true"></div>`;

    this.line1 = root.querySelector('#lightning-result-line1')!;
    this.line2 = root.querySelector('#lightning-result-line2')!;
    this.live = root.querySelector('#lightning-result-live')!;
    this.hide();
  }

  setBounds(bounds: ResultBlockBounds): void {
    this.root.style.left = `${bounds.left}px`;
    this.root.style.top = `${bounds.top}px`;
    this.root.style.width = `${bounds.width}px`;
    this.root.style.height = `${bounds.height}px`;
  }

  /** Measured host box — used by layout regression tests. */
  getLayoutRect(): { width: number; height: number; top: number; left: number } {
    const r = this.root.getBoundingClientRect();
    return {
      left: r.left,
      top: r.top,
      width: r.width || parseFloat(this.root.style.width) || this.root.offsetWidth,
      height: r.height || parseFloat(this.root.style.height) || this.root.offsetHeight,
    };
  }

  applyTranslations(): void {
    if (this.root.dataset.state === 'hint') {
      this.showHint();
    }
  }

  hide(): void {
    this.root.dataset.state = 'hidden';
    this.root.setAttribute('hidden', '');
    this.line1.textContent = '';
    this.line2.textContent = '';
    this.live.textContent = '';
  }

  showHint(): void {
    const t = this.i18n.t.bind(this.i18n);
    this.root.removeAttribute('hidden');
    this.root.dataset.state = 'hint';
    this.line1.textContent = t('dice.status.selectNumber');
    this.line2.textContent = '';
    this.root.classList.remove('lightning-result--rolling', 'lightning-result--dim');
  }

  /** Clears or dims the previous result while dice are rolling. */
  prepareRoll(): void {
    this.root.removeAttribute('hidden');
    this.root.dataset.state = 'rolling';
    this.root.classList.add('lightning-result--rolling', 'lightning-result--dim');
    this.line1.textContent = '';
    this.line2.textContent = '';
    this.live.textContent = '';
  }

  /** After dice settle: match count and base multiplier only (line 1). */
  showPartial(params: { matchCount: number; baseMultiplierTenths: number }): void {
    this.root.removeAttribute('hidden');
    this.root.dataset.state = 'partial';
    this.root.classList.remove('lightning-result--rolling', 'lightning-result--dim');
    this.line1.textContent = this.formatBaseLine(params.matchCount, params.baseMultiplierTenths);
    this.line2.textContent = '';
    this.live.textContent = this.line1.textContent;
  }

  /** Zero matches: both lines immediately. */
  showLoss(params: { payoutMinor: number; currency: string }): void {
    const t = this.i18n.t.bind(this.i18n);
    this.root.removeAttribute('hidden');
    this.root.dataset.state = 'loss';
    this.root.classList.remove('lightning-result--rolling', 'lightning-result--dim');
    this.line1.textContent = `${t('lightning.result.lossBase')} · ${t('lightning.result.notActivated')}`;
    this.line2.textContent = t('lightning.result.winOnly', {
      win: this.formatMoney(params.payoutMinor, params.currency),
    });
    this.announceAll();
  }

  /** After Lightning reel settles: combined line 1 and total + win on line 2. */
  showComplete(params: {
    matchCount: number;
    baseMultiplierTenths: number;
    lightningMultiplierTenths: number;
    totalMultiplierTenths: number;
    payoutMinor: number;
    currency: string;
  }): void {
    const t = this.i18n.t.bind(this.i18n);
    this.root.removeAttribute('hidden');
    this.root.dataset.state = 'complete';
    this.root.classList.remove('lightning-result--rolling', 'lightning-result--dim');
    const base = this.formatBaseLine(params.matchCount, params.baseMultiplierTenths);
    const lightning = t('lightning.result.lightningBonus', {
      bonus: formatLightningBonusTenths(params.lightningMultiplierTenths),
    });
    this.line1.textContent = `${base} · ${lightning}`;
    this.line2.textContent = t('lightning.result.totalWin', {
      total: formatMultiplierTenths(params.totalMultiplierTenths),
      win: this.formatMoney(params.payoutMinor, params.currency),
    });
    this.announceAll();
  }

  private formatBaseLine(matchCount: number, baseMultiplierTenths: number): string {
    const tp = this.i18n.tp.bind(this.i18n);
    return tp('lightning.result.matchBase', matchCount, {
      count: matchCount,
      base: formatMultiplierTenths(baseMultiplierTenths),
    });
  }

  private announceAll(): void {
    this.live.textContent = [this.line1.textContent, this.line2.textContent].filter(Boolean).join('. ');
  }
}
